import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';

export function BlogPostPage() {
  const { slug } = useParams();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Blog post data - in a real app, this would come from an API or CMS
  const getBlogPost = (slug: string | undefined) => {
    switch (slug) {
      case 'learnings-building-launching-larnu':
        return {
          title: "Learnings of building & launching LarnU 👽",
          category: "",
          date: "April 18, 2025",
          readTime: "8 min read",
          image: "https://i.imgur.com/dn9GIUP.jpg",
          content: `
            <p>Almost two years ago we shut down LarnU 👽 (the "duolingo" to learn to code) and set off on new paths with José Zúñiga Henríquez…</p>
            <br />
            
            <p>Closing was a painful process that leaves you stunned, suffering, and reflecting for months. Having your own startup means countless hours of work (70 + per week), effort, commitment, an emotional bond with what you're building, risk, plenty of highs and lows, and loneliness—it's a roller coaster! 🎢</p>
            <br />
            
            <p>Since we closed, I hadn't posted on LinkedIn until today. I'd like to briefly share my learnings, what I'd do differently, and what came after LarnU.</p>
            <br />
            
            <h2><strong>1️⃣ Pivot faster with a well-segmented user persona and an obsession with the problem</strong></h2>
            <p>When we decided to pivot, we tried to build on what we already had. Example: from "Duolingo to learn programming" → Quizify → "Duolingo to learn AI" → AI-powered conversation app to learn English. Every pivot recycled the same base. It's healthy to step out of that, validate without coding (when possible), and avoid being confined to the current product.</p>
            <br />
            
            <h2><strong>2️⃣ Raising capital isn't trivial</strong></h2>
            <p>It consumes a huge amount of a founder's time, and you have to get used to many NOs. It's vital to find the right momentum, stay consistent with investor updates, and let fundraising be the consequence of strong traction!</p>
            <br />
            
            <h2><strong>3️⃣ Trade-offs</strong></h2>
            <p>As the brilliant Pedro Pineda says, your startup is almost like a girlfriend—you'll devote a massive amount of time to it. I was lucky that Marcella Lozupone always supported and understood me, and was there in the toughest moments of this journey.</p>
            <br />
            
            <h2><strong>4️⃣ Focus</strong></h2>
            <p>Zero in on what will truly drive meaningful growth—product, strategy, marketing, networking, VC, etc. Make the 80/20 rule your commandment #1 and don't get distracted by PR and other things that don't move the needle!</p>
            <br />
            
            <h2><strong>5️⃣ B2C Edtech… whew, it's for the brave</strong></h2>
            <p>And it's not exactly sexy for VCs. More than once we heard: "You're a great team with years of B2B e-commerce SaaS experience—you know the problems, market, customers, know-how… why on earth did you jump into B2C Edtech?"</p>
            <br />
            
            <p>Because this post is about LarnU's closure, I also want to thank everyone who supported us—especially Platanus Ventures, Start-Up Chile, Google for Startups, Paula Enei, Agustín Feuerhake, Jaime Bünzli, Diego Stevens Ibáñez, Joaquín Stephens, Andrés Matte, Marcos Pueyrredon, and the entire team that made LarnU possible. Thank you! 🙌</p>
            <br />
            
            <h2>What am I up to now?</h2>
            <p>When we closed LarnU, I immediately joined the Buk 💙 team. While being mentored by Jaime at PV, I always admired the way he described Buk's culture, team, and challenges.</p>
            <br />
            
            <p>And finally… what do I do at Buk? I'm on an amazing, challenging team, leading the AI and People Analytics products as PM. Thanks for believing in me, Sebastián, Francisco, Felipe, Santiago, and Jaime. Let's give it our all!</p>
          `
        };
      default:
        return {
          title: "The Future of AI in Personal Productivity",
          category: "AI",
          date: "March 15, 2024",
          readTime: "5 min read",
          content: `
            <p>Artificial Intelligence is transforming how we approach personal productivity, offering unprecedented opportunities to streamline our daily workflows and enhance our decision-making capabilities.</p>
            
            <h2>The Current State of AI Tools</h2>
            <p>Today's AI productivity tools have evolved far beyond simple automation. From intelligent scheduling assistants to content generation platforms, these tools are becoming increasingly sophisticated in understanding context and user preferences.</p>
            
            <h2>Key Areas of Impact</h2>
            <p>AI is making significant strides in several areas:</p>
            <ul>
              <li><strong>Task Management:</strong> Smart prioritization and deadline management</li>
              <li><strong>Communication:</strong> Automated email responses and meeting summaries</li>
              <li><strong>Content Creation:</strong> Writing assistance and idea generation</li>
              <li><strong>Data Analysis:</strong> Insights from personal productivity metrics</li>
            </ul>
            
            <h2>Looking Ahead</h2>
            <p>The future promises even more integrated AI experiences, where our digital assistants will understand our work patterns, preferences, and goals to provide proactive support rather than reactive solutions.</p>
            
            <p>As we embrace these technologies, it's important to maintain a balance between efficiency and human creativity, ensuring that AI enhances rather than replaces our natural problem-solving abilities.</p>
          `
        };
    }
  };

  const blogPost = getBlogPost(slug);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <Link to="/blog">
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>
        </Link>
        
        {/* Featured Image */}
        {blogPost.image && (
          <div className="mb-8">
            <img 
              src={blogPost.image} 
              alt={blogPost.title}
              className="w-full h-64 md:h-80 object-cover rounded-lg shadow-lg"
            />
          </div>
        )}
        
        {blogPost.category && (
          <div className="mb-4">
            <span className="text-sm text-primary font-medium">
              {blogPost.category}
            </span>
          </div>
        )}
        
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          {blogPost.title}
        </h1>
        
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8">
          <span>{blogPost.date}</span>
          <span>•</span>
          <span>{blogPost.readTime}</span>
        </div>
      </div>
      
      <article className="prose prose-lg max-w-none">
        <div dangerouslySetInnerHTML={{ __html: blogPost.content }} />
      </article>
      
      <div className="mt-12 pt-8 border-t">
        <Link to="/blog">
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to All Posts
          </Button>
        </Link>
      </div>
    </div>
  );
}